import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import type React from "react";
import type { FC, ReactNode } from "react";
import { BADGE, FONT_SIZE, INK, LETTER_SPACING, NEUTRAL, STAT_TILE, SURFACE } from "../../../theme/tokens";
import { CommonDashboardBox } from "../../Dashboard/CommonDashboardBox";
import { AnimatedNumber } from "../Animations/AnimatedNumber";
import { clickableStatTileSx, STATE_ACCENT } from "./clickableStatTileSx";

/**
 * Whether the tile is currently driving a filter, and in which direction.
 *
 * `matching` = the surface below is showing the rows this tile counts; `not-matching` = it is
 * showing the inverse. Two states rather than one boolean because a tile that can invert has to say
 * which way it went — "selected" alone leaves the reader guessing what they are looking at.
 */
export type ClickableStatState = "default" | "matching" | "not-matching";

type ClickableStatTileProps = {
	label: string;
	value: number;
	icon?: ReactNode;
	state?: ClickableStatState;
	/**
	 * Already-translated badge text for the active state (e.g. "Filtering the table"). Omit and no
	 * badge renders — the accent still marks the tile as active.
	 */
	stateLabel?: string;
	onClick?: () => void;
	/**
	 * Renders an ✕ on the active tile. Give it whenever the tile applies a filter: the way out of a
	 * filter should be on the control that applied it, not somewhere else on the page.
	 */
	onClear?: () => void;
	/** Already-translated label for the ✕ (accessibility). */
	clearLabel?: string;
	/**
	 * Skeleton the figure instead of rendering it. Use it rather than showing a provisional number:
	 * a value that changes after it has been read is worse than one that arrives late, and a tile
	 * whose count moves while you are reaching for it is a tile you stop trusting.
	 */
	loading?: boolean;
	/** A mark before the label and figure, in a rounded square — a group's letter, a glyph. Makes it the tile's subject. */
	leading?: ReactNode;
	/** The leading square's colours. Omit for a quiet neutral square. */
	leadingColor?: { fill: string; ink: string };
	/** A hairline in this colour across the top edge, so a row of colour-coded tiles reads as a set. */
	accentColor?: string;
	/** Already-translated muted word after the figure ("staff"). */
	valueSuffix?: string;
	/** A dashed edge and no shadow: the tile counts what is left over (no group yet), not a set of its own. */
	dashed?: boolean;
	/** Position in its row: the tile fades in, staggered by the house step. Omit for no entrance. */
	entranceIndex?: number;
	/** Already-translated name for a clickable tile when the label alone doesn't say what the click does. */
	ariaLabel?: string;
	dataTestId?: string;
};

/**
 * A KPI tile that doubles as a filter control: a label, a count-up figure, an optional icon, and an
 * active state that says the surface below is currently filtered by it.
 *
 * Compose these in a responsive grid to form a KPI row. Keep the figure ABSOLUTE — a tile whose
 * value follows the filter it applies changes the moment you click it, which leaves nothing stable
 * to click back to and makes the tile read as a number one moment and a button the next.
 *
 * For a row of colour-coded buckets (holiday groups), give each tile a `leading` mark, its colours and an
 * `accentColor` hairline; `dashed` marks the leftover bucket. All are optional and off by default.
 */
export const ClickableStatTile: FC<ClickableStatTileProps> = ({
	label,
	value,
	icon,
	state = "default",
	stateLabel,
	onClick,
	onClear,
	clearLabel,
	loading = false,
	leading,
	leadingColor,
	accentColor,
	valueSuffix,
	dashed = false,
	entranceIndex,
	ariaLabel,
	dataTestId,
}) => {
	const clickable = !!onClick;
	const isActive = state !== "default";
	const accent = isActive ? STATE_ACCENT[state] : undefined;
	const showClear = isActive && !!onClear;

	const content = (
		<>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
				<Typography
					sx={{
						fontSize: FONT_SIZE.LABEL_XS,
						fontWeight: 600,
						color: INK.MUTED,
						lineHeight: 1.2,
						textTransform: "uppercase",
						letterSpacing: LETTER_SPACING.LABEL,
					}}
				>
					{label}
				</Typography>
				{showClear ? (
					<IconButton
						size="small"
						aria-label={clearLabel}
						onClick={(event) => {
							// The tile itself is a button; without this the clear would immediately re-apply it.
							event.stopPropagation();
							onClear();
						}}
						data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
						sx={{ p: 0.25, color: accent }}
					>
						<CloseIcon sx={{ fontSize: 14 }} />
					</IconButton>
				) : (
					icon && <Box sx={{ display: "flex", color: INK.MUTED, lineHeight: 0 }}>{icon}</Box>
				)}
			</Box>

			<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mt: 0.5 }}>
				{loading ? (
					<Skeleton variant="text" sx={{ width: 56, fontSize: FONT_SIZE.STAT_VALUE }} data-testid={dataTestId ? `${dataTestId}-skeleton` : undefined} />
				) : (
					<Typography
						sx={{
							fontSize: FONT_SIZE.STAT_VALUE,
							fontWeight: 700,
							color: INK.PRIMARY,
							lineHeight: 1,
							fontVariantNumeric: "tabular-nums",
							letterSpacing: "-0.02em",
						}}
					>
						<AnimatedNumber value={value} />
						{valueSuffix && (
							// A real space, not a margin, so screen readers say "4 staff" rather than "4staff".
							<Box component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 400, color: INK.MUTED, letterSpacing: "normal" }}>
								{` ${valueSuffix}`}
							</Box>
						)}
					</Typography>
				)}
				{isActive && stateLabel && (
					<Box
						sx={{
							px: 0.875,
							py: 0.25,
							borderRadius: BADGE.PILL_RADIUS_FULL,
							backgroundColor: accent,
							color: SURFACE,
							fontSize: FONT_SIZE.LABEL_MICRO,
							fontWeight: 700,
							lineHeight: 1.4,
							textTransform: "uppercase",
							letterSpacing: LETTER_SPACING.LABEL,
							whiteSpace: "nowrap",
						}}
						data-testid={dataTestId ? `${dataTestId}-state-badge` : undefined}
					>
						{stateLabel}
					</Box>
				)}
			</Box>
		</>
	);

	return (
		<CommonDashboardBox
			role={clickable ? "button" : undefined}
			tabIndex={onClick ? 0 : -1}
			aria-label={ariaLabel}
			data-testid={dataTestId}
			onClick={onClick}
			onKeyDown={
				onClick
					? (e: React.KeyboardEvent<HTMLDivElement>) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								onClick();
							}
						}
					: undefined
			}
			sx={clickableStatTileSx({ clickable, accent, dashed, accentColor, entranceIndex })}
		>
			{leading === undefined ? (
				content
			) : (
				<Box sx={{ display: "flex", flex: 1, alignItems: "center", gap: 1.5 }}>
					<Box
						aria-hidden
						sx={{
							width: STAT_TILE.LEADING_PX,
							height: STAT_TILE.LEADING_PX,
							flexShrink: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							borderRadius: 1,
							// DEFAULT: an uncoloured mark sits on a quiet neutral square.
							backgroundColor: leadingColor?.fill ?? NEUTRAL[150],
							color: leadingColor?.ink ?? INK.MUTED,
							fontSize: FONT_SIZE.TITLE_XL,
							fontWeight: 700,
						}}
					>
						{leading}
					</Box>
					<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, minWidth: 0 }}>{content}</Box>
				</Box>
			)}
		</CommonDashboardBox>
	);
};
